import React, { useEffect, useState } from "react";
import { login } from "../../api/Login";
import { errorHandler } from "../shared/Utils/ErrorHandler";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { userDataActions } from "../../store";
import { useLayout } from "../../App";

function LoginPage() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [loginError, setLoginError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const dispatch = useDispatch();
  const { setShowHeader, setShowFooter } = useLayout();

  // Shared className variables
  const inputClassName =
    "w-full px-3 max-[480px]:px-2 border border-[#7C7C7C] rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-sm max-[480px]:text-xs placeholder-gray-400 text-left h-8 max-[480px]:h-7";
  const labelClassName =
    "block text-sm max-[480px]:text-xs !font-semibold !font-montserrat mb-1 max-[480px]:mb-1 text-left";

  // Hide header and footer when component mounts
  useEffect(() => {
    setShowHeader(false);
    setShowFooter(false);

    // Restore header and footer when component unmounts
    return () => {
      setShowHeader(true);
      setShowFooter(true);
    };
  }, [setShowHeader, setShowFooter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = await login(formData);
      localStorage.setItem("userData", JSON.stringify(data.user));
      localStorage.setItem("refreshToken", data.refresh);
      localStorage.setItem("accessToken", data.access);
      dispatch(userDataActions.updateUserData(JSON.stringify(data.user)));

      let redirectTo = "/";
      if (
        data.user.user_type === "nonprofit_member" ||
        data.user.user_type === "nonprofit_admin"
      ) {
        redirectTo = "/profile";
      } else {
        redirectTo = state?.back || "/";
      }

      navigate(redirectTo);
    } catch (error) {
      const errorMessage = errorHandler(error);
      setLoginError(errorMessage);
      console.error("Error during login:", error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex flex-row max-[1200px]:p-1 h-screen max-[1200px]:flex-col max-[1200px]:gap-4 justify-start items-center overflow-hidden">
      {/* Form Section Container - Left Side (~64%) */}
      <div className="w-[64%] max-[1200px]:w-full h-screen flex items-center justify-center">
        {/* Form Section - Left Side */}
        <div className="max-[1200px]:w-full bg-white flex items-center justify-center p-4 w-full max-[768px]:p-3 max-[480px]:p-2">
          <div className="w-full max-w-lg max-[480px]:max-w-sm">
            {/* Back to Homepage button */}
            <button
              onClick={() => navigate("/")}
              className="absolute top-10 left-10 flex items-center gap-2 rounded-full bg-gray-200 px-3 py-1.5 hover:bg-gray-300 transition-colors"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Home</span>
            </button>

            <form
              onSubmit={handleSubmit}
              className="space-y-4 max-[480px]:space-y-3"
            >
              {/* Header */}
              <div className="mb-4 max-[480px]:mb-3 text-left">
                <p className="text-sm max-[480px]:text-xs !font-bold !font-poppins text-blue uppercase tracking-wide mb-2 max-[480px]:mb-1 text-left">
                  ACCOUNT
                </p>
                <h1 className="text-orange mb-2 max-[480px]:mb-1 text-left !font-[Poppins] !text-4xl max-[768px]:!text-3xl max-[480px]:!text-2xl !font-bold">
                  Login
                </h1>
                <p className="text-[#1B3A5D] !font-montserrat !text-sm max-[480px]:!text-xs !font-medium mb-2 max-[480px]:mb-1 text-left">
                  Welcome to Sponsor a Pet
                </p>
                <p className="!text-xs max-[480px]:!text-xs text-[#1B3A5D] !font-montserrat !font-medium text-left">
                  Don't have an account?{" "}
                  <Link
                    to="/signup"
                    className="text-[#46A4DD] underline !font-bold"
                  >
                    Sign Up
                  </Link>
                </p>
              </div>

              {/* Form Fields */}
              <div className="space-y-4 max-[540px]:space-y-3 max-[480px]:space-y-2">
                {/* Email Field */}
                <div>
                  <label className={labelClassName}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="username"
                    required
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Email"
                    className={inputClassName}
                  />
                </div>

                {/* Password Field */}
                <div>
                  <label className={labelClassName}>
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className={inputClassName}
                  />

                  {/* Forgot Password Link */}
                  <div className="text-right mt-1">
                    <Link
                      to="/forgot-password"
                      className="text-[#1B3A5D] text-xs underline !font-bold"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                </div>
              </div>

              {/* Error Messages */}
              {loginError && (
                <div className="text-red-500 text-sm mt-2 text-left">
                  {loginError}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full shadow-lg bg-gradient-to-r from-blue-400 to-blue-500 text-white py-3 px-6 rounded-2xl hover:from-blue-500 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-semibold text-sm mt-6 max-[480px]:mt-4 text-center"
              >
                Login
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Image Section - Right Side (~36%) - Show on larger screens */}
      <div className="max-[1200px]:w-full max-[1200px]:h-64 w-[36%] h-screen flex items-center justify-center max-[1200px]:hidden p-8 pr-48">
        <div className="w-full h-full">
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/jay-wennington-CdK2eYhWfQ0-unsplash.jpg"
            alt="Beautiful landscape image for login page"
            className="object-cover h-full w-full rounded-2xl shadow-lg"
          />
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
